/**
 * Making an Indonesian legal long-title readable in a list, WITHOUT rewriting it.
 *
 * Two measured problems on the real corpus (3,331 documents):
 *   43% of titles are ALL-CAPS — JDIH changed its own house style mid-2022, so the split
 *          is a year cohort, not noise: 2020-21 shout, 2023+ do not.
 *   67% run past 120 characters, the longest 487. They are legal long-titles; that is
 *          simply what they are.
 *
 * THIS IS A DISPLAY TRANSFORM. The stored title is never touched. It is the document's
 * legal identity, it appears in every citation, and a reader checking our answer against
 * jdih.acehprov.go.id needs the string to match theirs. #387 also embeds the stored title
 * into the retrieval prefix, so rewriting it would change what retrieval matches on.
 *
 * NO MODEL IS INVOLVED, and that is not a shortcut. `CLAUDE.md`'s rule is that a semantic
 * judgement — "does this text mean / support X" — must be model-made. Casing a word and
 * splitting a fixed grammar are neither. Indonesian legal titles have the form
 * `<Jenis> Nomor <N> Tahun <Y> tentang <Subject>`, which is a regex, not an inference —
 * and a paraphrased title in a citation would break traceability to the original.
 */

/**
 * Words that stay lowercase inside a title. Indonesian convention, and the reason a naive
 * `initcap` reads wrong: "Dana Bagi Hasil Pajak Kendaraan Bermotor Dan Pajak Air" should be
 * "… dan Pajak Air".
 */
const SMALL_WORDS = new Set([
  "dan",
  "atau",
  "yang",
  "pada",
  "dari",
  "untuk",
  "dalam",
  "atas",
  "serta",
  "di",
  "ke",
  "tentang",
  "bagi",
  "dengan",
  "kepada",
  "sebagai",
  "antara",
  "terhadap",
  "sampai",
  "beserta",
]);

/**
 * Acronyms that must stay upper-case. HARVESTED FROM THE CORPUS, not invented: in a
 * MIXED-case title a capital is informative, so every all-caps token in those titles is a
 * genuine acronym. That query returned DPRK, PPID, LKPJ, DPRA, APBA, APBK, PPNS, LAKIP,
 * SOP and DIP — plus noise (`TAHUN`, `ACEH`, `RUMAH`) which a human struck out.
 *
 * The list is necessary because in an ALL-CAPS title case carries no information at all:
 * "APBA" and "TENTANG" look identical to a formatter, so one of them has to be known.
 * An unknown acronym degrades to Title Case — wrong but readable — never to nonsense.
 */
const ACRONYMS = new Set([
  "DPRK",
  "DPRA",
  "DPR",
  "PPID",
  "LKPJ",
  "APBA",
  "APBK",
  "APBD",
  "PPNS",
  "LAKIP",
  "SOP",
  "DIP",
  "SK",
  "PT",
  "RPJMD",
  "RPJPD",
  "RTRW",
  "SKPA",
  "SKPD",
  "UPTD",
  "BLUD",
  "BUMD",
  "KUA",
  "PPAS",
  "KIP",
  "PLTA",
  "TIK",
  "SPBE",
  "ASN",
  "PNS",
  "THR",
  "COVID",
  "KTP",
  "NIK",
  "PAD",
  "DAU",
  "DAK",
  "SMA",
  "SMK",
  "SLB",
]);

/** Roman numerals used for amendment ordinals ("Perubahan II"). */
const ROMAN = /^[IVXL]+$/;

/**
 * One token, cased. `index` matters because a small word is only lowercased mid-title —
 * a title may legitimately begin with one.
 */
function caseToken(token: string, index: number): string {
  const letters = token.replace(/[^A-Za-z/]/g, "");
  if (!letters) return token;
  const upper = letters.toUpperCase();
  if (ACRONYMS.has(upper) || (ROMAN.test(upper) && upper.length > 1)) {
    return token.toUpperCase();
  }
  if (index > 0 && SMALL_WORDS.has(letters.toLowerCase())) return token.toLowerCase();
  // Preserve inner punctuation: "kabupaten/kota" must case BOTH halves.
  return token
    .toLowerCase()
    .replace(/(^|[^a-z])([a-z])/g, (_match, before: string, letter: string) => before + letter.toUpperCase());
}

/** A whole title (or fragment), cased for display. Idempotent on already-mixed titles. */
export function titleCaseLegal(text: string): string {
  return text.trim().split(/\s+/).map(caseToken).join(" ");
}

export interface LegalTitleParts {
  /** "Peraturan Gubernur Aceh 15/2018" — the citable short form, or null if unparsable. */
  label: string | null;
  /** What the instrument is ABOUT: everything after "tentang". Falls back to the title. */
  subject: string;
  /** The full official title, cased but otherwise untouched. For tooltips and citations. */
  full: string;
}

/**
 * Split a legal long-title into a short label and its subject.
 *
 * Indonesian legal titles follow `<Jenis> Nomor <N> Tahun <Y> tentang <Subject>`, so this
 * is a parse and not a summary. A title that does NOT follow it — an uploaded file, a
 * `Berita Acara`, a keyless fixture — returns `label: null` and keeps its whole text as the
 * subject, rather than being forced into a shape it does not have.
 *
 * Only the FIRST "tentang" splits: amendment titles contain a second one ("Perubahan atas
 * Qanun … tentang Baitul Mal"), and that belongs to the subject.
 */
export function parseLegalTitle(rawTitle: string): LegalTitleParts {
  const title = (rawTitle ?? "").trim();
  const full = titleCaseLegal(title);
  const match = title.match(/^(.*?)\s+nomor\s+(\S+?)\s+tahun\s+(\d{4})\s+tentang\s+(.*)$/i);
  // Every group is non-optional in the pattern, so a match guarantees all four. TypeScript
  // cannot see that, and defaulting is honest here: an empty group would only mean the
  // pattern changed, and an empty label is caught by the guard below rather than rendering
  // as a stray "/2018".
  const jenis = match?.[1] ?? "";
  const nomor = match?.[2] ?? "";
  const tahun = match?.[3] ?? "";
  const subject = match?.[4] ?? "";
  if (!match || !jenis || !nomor || !tahun) return { label: null, subject: full, full };
  return {
    label: `${titleCaseLegal(jenis)} ${nomor.replace(/[.,]$/, "")}/${tahun}`,
    subject: titleCaseLegal(subject),
    full,
  };
}

/**
 * Trim to a length that fits one line, cutting at a word boundary.
 *
 * The ellipsis is load-bearing: it is what tells a reader the title continues, so a
 * truncated title is never mistaken for a short one. The full string always remains
 * available on hover — a picker that silently shortened a title would make two amendments
 * of the same regulation indistinguishable.
 */
export function truncateAtWord(text: string, maxLength = 110): string {
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
